import React, { useState, useRef, useEffect } from 'react';

/**
 * Tri-state variable-rate classification badge with a click-to-set popover.
 *   isVariable === true  -> "VR"           (green pill)
 *   isVariable === false -> "Not variable" (gray pill)
 *   isVariable === null  -> "Unclassified" (amber pill)
 * Clicking opens a small menu (VR / Not variable / Unclassified); choosing an
 * option calls onSet(true|false|null). The parent owns the PUT + re-render, so
 * this component holds no server state. `disabled` suppresses the menu while a
 * save is in flight.
 */
const LABELS = [
  { value: true, label: 'VR' },
  { value: false, label: 'Not variable' },
  { value: null, label: 'Unclassified' },
];

const pillCls = (isVariable) => {
  if (isVariable === true)
    return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200';
  if (isVariable === false)
    return 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200';
  return 'bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-200';
};

const labelFor = (isVariable) =>
  LABELS.find((o) => o.value === isVariable)?.label ?? 'Unclassified';

const VrClassificationBadge = ({ isVariable, onSet, disabled = false }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  const choose = (value) => {
    setOpen(false);
    onSet(value);
  };

  return (
    <div className="relative inline-block" ref={ref}>
      <button
        type="button"
        disabled={disabled}
        onClick={(e) => {
          e.stopPropagation();
          if (!disabled) setOpen((v) => !v);
        }}
        className={`inline-block text-xs px-2.5 py-1 rounded-full ${pillCls(isVariable)} ${
          disabled ? 'opacity-50' : 'cursor-pointer'
        }`}
      >
        {labelFor(isVariable)}
      </button>
      {open && (
        <div
          role="menu"
          className="absolute z-20 mt-1 min-w-[9rem] rounded-md border bg-white text-gray-900 shadow-lg dark:bg-gray-800 dark:text-white dark:border-gray-700"
          onClick={(e) => e.stopPropagation()}
        >
          {LABELS.map((o) => (
            <button
              key={String(o.value)}
              type="button"
              role="menuitem"
              onClick={() => choose(o.value)}
              className="block w-full px-3 py-1.5 text-left text-sm hover:bg-gray-100 dark:hover:bg-gray-700"
            >
              {o.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default VrClassificationBadge;
